export function EdmontonEnforcement() {
  const violations = [
    {
      violation: 'Operating without business license',
      penalty: '$500 - $1,500',
      enforcement: 'Warning then daily fines',
      severity: 'high'
    },
    {
      violation: 'Noise violations',
      penalty: '$500+',
      enforcement: 'Escalating penalties',
      severity: 'high'
    },
    {
      violation: 'Parking violations',
      penalty: '$250 - $750',
      enforcement: 'Warning then fines',
      severity: 'medium'
    },
    {
      violation: 'Trash/waste violations',
      penalty: '$500+',
      enforcement: 'Immediate fines',
      severity: 'high'
    },
    {
      violation: 'Missing Guest Management Plan',
      penalty: '$250 - $1,000',
      enforcement: 'License suspension risk',
      severity: 'medium'
    },
    {
      violation: 'Fire safety non-compliance',
      penalty: '$1,000 - $5,000',
      enforcement: 'License suspension',
      severity: 'high'
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="inline-block bg-[#882629]/10 text-[#882629] px-4 py-2 rounded-full text-sm font-medium mb-4">
            2026 Enforcement
          </span>
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Nuisance-Focused Enforcement
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Edmonton's enforcement focuses on "nuisance" properties that create problems for neighbors. Penalties for noise, parking, and trash violations are steep at $500+, but compliant operators face minimal scrutiny.
          </p>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-8 text-center">
            Common Violations &amp; Penalties
          </h3>
          <div className="bg-[#F0F0F0] rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#882629] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Violation Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Penalty Range</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Enforcement Action</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Severity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white">
                  {violations.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-[#F0F0F0]'}>
                      <td className="px-6 py-4 text-sm text-[#505A5A] font-medium">{item.violation}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-[#882629]">{item.penalty}</td>
                      <td className="px-6 py-4 text-sm text-[#505A5A]/70">{item.enforcement}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          item.severity === 'critical' ? 'bg-red-100 text-red-800' :
                          item.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.severity.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-[#F0F0F0] rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-[#882629] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-volume-down-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-semibold text-[#505A5A] mb-4">Noise Control</h3>
            <p className="text-[#505A5A]/70 leading-relaxed mb-4">
              Strict enforcement of noise bylaws with penalties of $500+ for violations. Quiet hours typically 10pm-7am.
            </p>
            <div className="text-2xl font-bold text-[#882629]">$500+</div>
          </div>

          <div className="bg-[#F0F0F0] rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-[#882629] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-parking-box-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-semibold text-[#505A5A] mb-4">Parking Compliance</h3>
            <p className="text-[#505A5A]/70 leading-relaxed mb-4">
              Guest parking must comply with zoning requirements. Street parking violations result in warnings then fines.
            </p>
            <div className="text-2xl font-bold text-[#882629]">$250-$750</div>
          </div>

          <div className="bg-[#F0F0F0] rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-[#882629] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-delete-bin-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-semibold text-[#505A5A] mb-4">Waste Management</h3>
            <p className="text-[#505A5A]/70 leading-relaxed mb-4">
              Improper trash disposal or overflowing bins result in immediate fines. Maintain clean property appearance.
            </p>
            <div className="text-2xl font-bold text-[#882629]">$500+</div>
          </div>
        </div>

        <div className="bg-[#F0F0F0] rounded-2xl p-8 mb-12">
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-6 flex items-center gap-3">
            <i className="ri-file-text-line text-[#882629] text-3xl"></i>
            Guest Management Plan Requirements
          </h3>
          <p className="text-[#505A5A]/70 mb-6 leading-relaxed">
            Edmonton requires a comprehensive Guest Management Plan to prevent nuisance issues. Your plan must address:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-[#505A5A] mb-3">Required Elements</h4>
              <ul className="space-y-2 text-[#505A5A]/70 text-sm">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                  <span>Parking arrangements and guest instructions</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                  <span>Noise control policies and quiet hours</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                  <span>24/7 emergency contact information</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                  <span>Waste management and disposal procedures</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-[#505A5A] mb-3">Best Practices</h4>
              <ul className="space-y-2 text-[#505A5A]/70 text-sm">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                  <span>Provide neighbors with your contact information</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                  <span>Respond to complaints within 30 minutes</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                  <span>Screen guests and set clear house rules</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                  <span>Regular property inspections and maintenance</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8 mb-12">
          <div className="flex items-start gap-4">
            <i className="ri-thumb-up-line text-3xl text-green-600 flex-shrink-0"></i>
            <div>
              <h4 className="text-xl font-bold text-green-900 mb-3">
                Investor-Friendly Enforcement Approach
              </h4>
              <p className="text-green-800 leading-relaxed">
                Edmonton's enforcement focuses on problem properties, not compliant operators. If you maintain good neighbor relations, keep your property well-managed, and respond quickly to any concerns, you'll face minimal scrutiny. This makes Edmonton one of the most operator-friendly cities in Canada.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#882629] to-[#6E1F22] rounded-2xl p-8 lg:p-10 text-white shadow-xl">
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <i className="ri-user-heart-line text-6xl"></i>
            </div>
            <div className="flex-grow text-center lg:text-left">
              <h4 className="text-2xl font-bold mb-3">
                Enforcement Style: Nuisance-Focused
              </h4>
              <p className="text-lg text-white/90 leading-relaxed">
                Edmonton focuses enforcement on "nuisance" properties that create problems for neighbors. Penalties for noise, parking, and trash violations are steep at $500+. However, well-managed properties with good neighbor relations face minimal scrutiny, making Edmonton highly investor-friendly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
